import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowRight, Check, Eye, EyeOff, Loader2 } from "lucide-react";

import { AuthBrandColumn } from "@/components/auth/AuthBrandColumn";
import { AuthBrandMark } from "@/components/auth/AuthBrandMark";
import { AuthStatusScreen } from "@/components/auth/AuthStatusScreen";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { cn } from "@/lib/utils";
import { resetPasswordWithToken } from "@/services/authService";

interface PasswordRules {
  len: boolean;
  case: boolean;
  num: boolean;
  spec: boolean;
}

function evaluateRules(password: string): PasswordRules {
  return {
    len: password.length >= 8,
    case: /[a-z]/.test(password) && /[A-Z]/.test(password),
    num: /\d/.test(password),
    spec: /[^A-Za-z0-9]/.test(password),
  };
}

export function ResetPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const rules = evaluateRules(newPassword);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t("auth.resetPassword.mismatch", "Las contraseñas no coinciden."));
      return;
    }
    if (!token) {
      toast.error(
        t(
          "auth.resetPassword.invalidOrExpired",
          "El enlace es inválido o expiró. Pedí uno nuevo.",
        ),
      );
      return;
    }
    setIsSubmitting(true);
    try {
      await resetPasswordWithToken(token, newPassword);
      toast.success(
        t(
          "auth.resetPassword.success",
          "Contraseña actualizada. Iniciá sesión con la nueva.",
        ),
      );
      navigate("/login", { replace: true });
    } catch {
      toast.error(
        t(
          "auth.resetPassword.invalidOrExpired",
          "El enlace es inválido o expiró. Pedí uno nuevo.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthStatusScreen
        state="error"
        title={t("auth.resetPassword.invalidTitle", "Enlace inválido o expirado")}
        message={t(
          "auth.resetPassword.invalidOrExpired",
          "El enlace es inválido o expiró. Pedí uno nuevo.",
        )}
        action={
          <>
            <Link to="/login" className="fx-auth-btn fx-auth-btn-ghost">
              {t("auth.resetPassword.backToLogin", "Volver")}
            </Link>
            <Link
              to="/auth/forgot-password"
              className="fx-auth-btn fx-auth-btn-primary"
            >
              {t("auth.resetPassword.requestNew", "Pedir nuevo enlace")}
            </Link>
          </>
        }
      />
    );
  }

  return (
    <div className="fx-auth-stage">
      <div className="lang-switch">
        <LanguageSwitcher />
      </div>

      <div className="fx-auth-card">
        <div className="fx-auth-form-col">
          <div className="fx-auth-form-inner">
            <AuthBrandMark />
            <span className="fx-auth-eyebrow">
              {t("auth.resetPassword.eyebrow", "Casi listo")}
            </span>
            <h1 className="fx-auth-h1">
              {t("auth.resetPassword.titleLead", "Creá una")}{" "}
              <span className="ital">
                {t("auth.resetPassword.titleAccent", "contraseña nueva.")}
              </span>
            </h1>
            <p className="fx-auth-lead">
              {t(
                "auth.resetPassword.subtitle",
                "Elegí una contraseña segura para tu cuenta.",
              )}
            </p>

            <form className="fx-auth-form" onSubmit={handleSubmit}>
              <div className="fx-auth-field">
                <label htmlFor="newPassword">
                  {t("auth.resetPassword.newPassword", "Nueva contraseña")}
                </label>
                <div className="fx-auth-field-wrap">
                  <input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    className="show"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword
                        ? t("auth.login.hidePassword", "Ocultar contraseña")
                        : t("auth.login.showPassword", "Mostrar contraseña")
                    }
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
                <div className="fx-auth-rules">
                  <div className={cn("fx-auth-rule", rules.len && "ok")}>
                    <span className="check">{rules.len ? <Check /> : null}</span>
                    {t("auth.register.passwordStrength.minLength")}
                  </div>
                  <div className={cn("fx-auth-rule", rules.case && "ok")}>
                    <span className="check">{rules.case ? <Check /> : null}</span>
                    {t("auth.register.passwordStrength.uppercase")}
                  </div>
                  <div className={cn("fx-auth-rule", rules.num && "ok")}>
                    <span className="check">{rules.num ? <Check /> : null}</span>
                    {t("auth.register.passwordStrength.number")}
                  </div>
                  <div className={cn("fx-auth-rule", rules.spec && "ok")}>
                    <span className="check">{rules.spec ? <Check /> : null}</span>
                    {t("auth.register.passwordStrength.special")}
                  </div>
                </div>
              </div>

              <div className="fx-auth-field">
                <label htmlFor="confirmPassword">
                  {t("auth.resetPassword.confirmPassword", "Confirmá la contraseña")}
                </label>
                <div className="fx-auth-field-wrap">
                  <input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="fx-auth-btn fx-auth-btn-primary"
                disabled={
                  isSubmitting ||
                  newPassword.length < 8 ||
                  confirmPassword.length < 8
                }
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : (
                  <>
                    {t("auth.resetPassword.submit", "Actualizar contraseña")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>

              <div className="fx-auth-alt" style={{ marginTop: 4 }}>
                <Link to="/login">
                  {t("auth.resetPassword.backToLogin", "Volver a iniciar sesión")}
                  <ArrowRight aria-hidden />
                </Link>
              </div>
            </form>
          </div>
        </div>

        <AuthBrandColumn mode="login" />
      </div>
    </div>
  );
}
